import { useCallback, useEffect, useMemo, useState } from "react";
import jwtDecode from "jwt-decode";
import leadDashboardService from "../services/leadDashboard.service";

const ROLE_KEY = "leadDashRole";
const PERIOD_KEY = "leadDashPeriod";
const ALL_ROLES = ["manager", "agent", "admin"];
const PERIODS = ["today", "yesterday", "7d", "30d"];

// Map the backend's snake_case payload into the {label, value} shapes the
// chart primitives expect, so the screen stays declarative.
const toLabelValue = (arr, labelKey) =>
  (arr || []).map((d) => ({ label: d[labelKey], value: d.count }));

function getInitial(key, allowed, fallback) {
  const saved = localStorage.getItem(key);
  if (allowed.includes(saved)) return saved;
  return allowed[0] || fallback;
}

function useLeadDashboard(allowedRoles = ALL_ROLES) {
  const [role, setRoleState] = useState(() => getInitial(ROLE_KEY, allowedRoles, "manager"));
  const [period, setPeriodState] = useState(() => getInitial(PERIOD_KEY, PERIODS, "today"));
  const [source, setSource] = useState("All");
  const [owner, setOwner] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refetchFlag, setRefetchFlag] = useState(0);
  const [timer, setTimer] = useState(30);

  const [data, setData] = useState({
    kpis: null,
    funnel: [],
    segments: [],
    sourcesData: [],
    states: [],
    actionsTrend: [],
    conversionsTrend: [],
    conversions: [],
    feed: [],
    agenda: [],
    leaderboard: [],
    byOwner: [],
  });

  const [sourceOptions, setSourceOptions] = useState([]);

  const agentPhone = useMemo(() => {
    try {
      const token = localStorage.getItem("refreshToken");
      return token ? jwtDecode(token)?.phone : undefined;
    } catch {
      return undefined;
    }
  }, []);

  const setRole = useCallback(
    (next) => {
      if (!allowedRoles.includes(next)) return;
      localStorage.setItem(ROLE_KEY, next);
      setRoleState(next);
    },
    [allowedRoles],
  );

  const setPeriod = useCallback((next) => {
    if (!PERIODS.includes(next)) return;
    localStorage.setItem(PERIOD_KEY, next);
    setPeriodState(next);
  }, []);

  useEffect(() => {
    if (!allowedRoles.includes(role)) setRoleState(allowedRoles[0] || "manager");
  }, [allowedRoles, role]);

  const refetch = useCallback(() => setRefetchFlag((n) => n + 1), []);

  useEffect(() => {
    const id = setInterval(() => setTimer((t) => (t <= 1 ? 30 : t - 1)), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const params = { role, period };
    if (source && source !== "All") params.source = source;
    if (owner && owner !== "All") params.owner = owner;
    if (role === "agent" && agentPhone) params.agent_phone = agentPhone;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setTimer(30);

        const requests = [
          leadDashboardService.getDashboardStats(params),
          leadDashboardService.getFollowups(params),
          role === "agent"
            ? Promise.resolve({ success: true, agents: [] })
            : leadDashboardService.getLeaderboard(params),
        ];
        const [stats, followups, board] = await Promise.all(requests);
        if (cancelled) return;

        if (!stats?.success) {
          setError(stats?.error || "Failed to load dashboard");
          setLoading(false);
          return;
        }

        setData({
          kpis: stats.kpis,
          funnel: toLabelValue(stats.funnel, "stage"),
          segments: toLabelValue(stats.by_segment, "segment"),
          sourcesData: (stats.by_source || []).map((d) => ({
            label: d.source,
            value: d.count,
            rate: d.conversion_rate,
          })),
          states: toLabelValue(stats.by_state, "state"),
          actionsTrend: stats.actions_trend || [],
          conversionsTrend: stats.conversions_trend || [],
          conversions: stats.conversions || [],
          feed: stats.feed || [],
          agenda: followups?.success ? followups.followups || [] : [],
          leaderboard: board?.success ? board.agents || [] : [],
          byOwner: (stats.by_owner || []).map((d) => ({
            label: d.owner,
            value: d.count,
            id: d.owner_id,
          })),
        });

        if (source === "All") {
          const unique = [
            ...new Set((stats.by_source || []).map((s) => s.source).filter(Boolean)),
          ];
          setSourceOptions(unique);
        }
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setError("Failed to load dashboard");
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [role, period, source, owner, agentPhone, refetchFlag]);

  return {
    role,
    setRole,
    period,
    setPeriod,
    source,
    setSource,
    owner,
    setOwner,
    loading,
    error,
    refetch,
    timer,
    sourceOptions,
    ...data,
  };
}

export default useLeadDashboard;
